import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../Components/SnackBar";
import { apiPostRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { missingCategoryPaperStyle } from "./styles";

const MissingCategoryModal = ({ open, onClose, vertical = "products" }) => {
  const api = endpoints.catalog(vertical);
  const { t } = useTranslation("supplier-products");
  const snackbar = useSnackbar();
  const [categoryText, setCategoryText] = useState("");

  const handleSubmit = async () => {
    if (!categoryText.trim()) return;

    try {
      const response = await apiPostRequest(api.missingCategory, {
        category_name: categoryText.trim(),
      });

      if (response.status === 200) {
        snackbar(t("missing_category_modal.success_message"), "success");
        onClose();
      }
    } catch (error) {
      console.error("Error submitting missing category:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      disableEscapeKeyDown
      PaperProps={{ sx: missingCategoryPaperStyle }}
      fullWidth
      maxWidth="sm"
      // Higher z-index so it sits on top of the product modal
      sx={{ zIndex: 1301 }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #EAEAEA",
          px: "24px",
          py: "16px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#2F2B3DE5",
          }}
        >
          {t("missing_category_modal.title")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: "19px" }} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ pt: "24px", pb: "16px" }}>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            color: "#2F2B3DE5",
            mb: "12px",
          }}
        >
          {t("missing_category_modal.question")}
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={categoryText}
          onChange={(e) => setCategoryText(e.target.value)}
          placeholder={t("missing_category_modal.placeholder")}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: "14px",
              "& fieldset": { borderColor: "#EAEAEA" },
              "&:hover fieldset": { borderColor: "#46a091" },
              "&.Mui-focused fieldset": { borderColor: "#46a091" },
            },
          }}
        />
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: "24px",
          py: "16px",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: "#6C727F",
            color: "#fff",
            fontSize: "14px",
            borderRadius: "10px",
            px: "24px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#5b5f6b", boxShadow: "none" },
          }}
        >
          {t("missing_category_modal.back")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!categoryText.trim()}
          sx={{
            backgroundColor: "#248B70",
            color: "#fff",
            borderRadius: "10px",
            fontSize: "14px",
            px: "24px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#1f7b63", boxShadow: "none" },
            "&:disabled": { backgroundColor: "#e0e0e0" },
          }}
        >
          {t("missing_category_modal.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingCategoryModal;
